import { useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Input, Label, FormGroup, Button, Badge, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useManualAllowlist, useEffectiveAllowlist, useAddAllowlistIp, useRemoveAllowlistIp,
} from './useGatewayAllowlist';

/**
 * Gateway-Allowlist für die exponierte CrowdSec-API (cs.api.valitype.net).
 * Manuelle IP-Freigaben + Vorschau der effektiven Allowlist (manuell +
 * NetBox-Tag-IPs), die der Kong-Sync-CronJob rendert.
 */
export function NexusGatewayAllowlistPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');

  const manual = useManualAllowlist();
  const effective = useEffectiveAllowlist();
  const add = useAddAllowlistIp();
  const remove = useRemoveAllowlistIp();

  const manualItems = manual.data?.items ?? [];
  const eff = effective.data;

  const onAdd = async () => {
    if (!value.trim()) return;
    await add.mutateAsync({ value: value.trim(), label: label.trim() || undefined });
    setValue(''); setLabel('');
  };

  return (
    <div>
      <PageHeader title="CrowdSec · Gateway-Allowlist" subtitle="IP-Freigaben für cs.api.valitype.net" />
      <Alert color="info" className="small">
        Nur IPs auf dieser Allowlist erreichen die exponierte CrowdSec-API
        (<code>/api</code> + <code>/appsec</code>). Die effektive Liste ist
        die Summe aus diesen manuellen Einträgen und den in NetBox mit Tag
        <code>cs-allowlist</code> markierten IPs. Ein Kong-Sync-CronJob rendert
        sie regelmäßig in die Gateway-ip-restriction.
      </Alert>

      <Row>
        <Col md={7}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">Manuelle Freigaben</CardTitle>
              {canWrite && (
                <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                  <FormGroup className="mb-0">
                    <Label className="small text-muted mb-1">IP oder CIDR</Label>
                    <Input bsSize="sm" value={value} onChange={(e) => setValue(e.target.value)} placeholder="203.0.113.10 oder 10.0.0.0/24" style={{ minWidth: 220 }} />
                  </FormGroup>
                  <FormGroup className="mb-0">
                    <Label className="small text-muted mb-1">Label (optional)</Label>
                    <Input bsSize="sm" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. Büro" style={{ minWidth: 160 }} />
                  </FormGroup>
                  <Button size="sm" color="primary" disabled={!value.trim() || add.isPending} onClick={onAdd}>Hinzufügen</Button>
                </div>
              )}
              <DataTable
                loading={manual.isLoading}
                error={manual.error}
                empty={!manual.isLoading && manualItems.length === 0}
                emptyMessage="Keine manuellen Freigaben."
                onRetry={() => manual.refetch()}
              >
                <thead>
                  <tr><th>IP / CIDR</th><th>Label</th><th style={{ width: 130 }}>Angelegt</th><th className="text-end">Aktion</th></tr>
                </thead>
                <tbody>
                  {manualItems.map((it) => (
                    <tr key={it.id}>
                      <td className="font-monospace small">{it.cidr}</td>
                      <td className="small">{it.label || <span className="text-muted">—</span>}</td>
                      <td>{it.created_at ? <RelativeTime iso={it.created_at} /> : '—'}</td>
                      <td className="text-end">
                        {canWrite && (
                          <Button size="sm" color="danger" outline disabled={remove.isPending} onClick={() => remove.mutateAsync({ id: it.id })}>Entfernen</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">
                Effektive Allowlist{' '}
                {eff && <Badge color="secondary" pill>{eff.allowlist?.length ?? 0}</Badge>}
              </CardTitle>
              {eff?.sources && (
                <div className="small text-muted mb-2">
                  manuell: {eff.sources.manual} · NetBox: {eff.sources.netbox}
                  {eff.sources.netboxError && <span className="text-danger"> · NetBox-Fehler: {eff.sources.netboxError}</span>}
                </div>
              )}
              <div style={{ maxHeight: 360, overflowY: 'auto' }} className="font-monospace small">
                {(eff?.allowlist ?? []).map((ip) => <div key={ip}>{ip}</div>)}
                {effective.isLoading && <div className="text-muted">lädt…</div>}
                {!effective.isLoading && (eff?.allowlist?.length ?? 0) === 0 && <div className="text-muted">leer</div>}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
